import { jsPDF } from "jspdf";
import logo from "assets/images/logos/porteriq-logo-insights.png";

export const generateReportPDF = async (report, competitors, selectedCompetitor) => {
  if (!report) return;

  try {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 20;

    const checkPageBreak = (heightNeeded) => {
      if (yPos + heightNeeded > pageHeight - margin) {
        pdf.addPage();
        yPos = 20;
        return true;
      }
      return false;
    };

    const headerHeight = 40;
    pdf.setFillColor(15, 21, 53);
    pdf.rect(0, 0, pageWidth, headerHeight, "F");

    pdf.setDrawColor(37, 99, 235);
    pdf.setLineWidth(0.5);
    pdf.line(0, headerHeight, pageWidth, headerHeight);

    try {
      const img = new Image();
      img.src = logo;
      await new Promise((resolve) => {
        if (img.complete) resolve();
        else {
          img.onload = resolve;
          img.onerror = resolve;
        }
      });

      let logoWidth = 60;
      let logoHeight = 15;

      if (img.width && img.height) {
        const ratio = img.width / img.height;
        logoHeight = logoWidth / ratio;
        const maxHeight = 25;
        if (logoHeight > maxHeight) {
          logoHeight = maxHeight;
          logoWidth = logoHeight * ratio;
        }
      }

      const logoY = (headerHeight - logoHeight) / 2;
      pdf.addImage(logo, "PNG", margin, logoY, logoWidth, logoHeight);
    } catch (e) {
      console.warn("Logo processing error", e);
      pdf.addImage(logo, "PNG", margin, 10, 50, 12.5);
    }

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont("helvetica", "bold");

    const competitorName = competitors?.find(c => c.id === selectedCompetitor)?.name || selectedCompetitor || "Competitor";

    if (selectedCompetitor === "market_overview") {
      pdf.text("Market Overview Report", pageWidth - margin, headerHeight / 2, { align: "right" });
    } else {
      pdf.text("Competitor Report:", pageWidth - margin, 15, { align: "right" });
      pdf.text(competitorName, pageWidth - margin, 22, { align: "right" });
    }

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(200, 200, 200);
    pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth - margin, 28, { align: "right" });

    yPos = 55;

    pdf.setFillColor(248, 250, 252);
    pdf.setDrawColor(226, 232, 240);

    const boxPadding = 6;
    const textWidth = contentWidth - (boxPadding * 2) - 4;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");

    const headlineLines = pdf.splitTextToSize(report.headline, textWidth);
    const headlineHeight = headlineLines.length * 6;
    const boxHeight = headlineHeight + (boxPadding * 2);

    pdf.rect(margin, yPos, contentWidth, boxHeight, "FD");

    pdf.setFillColor(37, 99, 235);
    pdf.rect(margin, yPos, 2, boxHeight, "F");

    pdf.setTextColor(30, 41, 59);
    pdf.text(headlineLines, margin + 6, yPos + boxPadding + 4);

    yPos += boxHeight + 15;

    const addSection = (title, content) => {
      if (!content) return;

      checkPageBreak(30);

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(37, 99, 235);
      pdf.text(title.toUpperCase(), margin, yPos);

      pdf.setDrawColor(226, 232, 240);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);

      yPos += 8;

      pdf.setTextColor(51, 65, 85);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");

      const cleanContent = content.replace(/\*\*/g, "").replace(/##/g, "");
      const lines = pdf.splitTextToSize(cleanContent, contentWidth);

      const lineHeight = 5;

      lines.forEach(line => {
        if (checkPageBreak(lineHeight)) {
        }
        pdf.text(line, margin, yPos);
        yPos += lineHeight;
      });

      yPos += 12;
    };

    // Highlights summary line
    if (report.highlights) {
      const h = report.highlights;
      let highlightLine = "";
      if (selectedCompetitor === "market_overview") {
        highlightLine = [
          h.mostThreateningCompetitor && `Most Threatening: ${h.mostThreateningCompetitor}`,
          h.dominantMarketTheme && `Theme: ${h.dominantMarketTheme}`,
          h.totalActiveTrials && `Active Trials: ${h.totalActiveTrials}`,
          h.marketHiringPosture && `Hiring: ${h.marketHiringPosture}`,
        ].filter(Boolean).join("  |  ");
      } else {
        highlightLine = [
          h.threatLevelBadge && `Threat: ${h.threatLevelBadge}`,
          h.activeTrialsCount && `Trials: ${h.activeTrialsCount}`,
          h.topContentTheme && `Theme: ${h.topContentTheme}`,
          h.hiringSignal && `Hiring: ${h.hiringSignal}`,
          h.reportPeriod && `Period: ${h.reportPeriod}`,
        ].filter(Boolean).join("  |  ");
      }
      if (highlightLine) {
        checkPageBreak(12);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(100, 116, 139);
        const hLines = pdf.splitTextToSize(highlightLine, contentWidth);
        hLines.forEach(line => {
          pdf.text(line, margin, yPos);
          yPos += 4.5;
        });
        yPos += 8;
      }
    }

    addSection("Intelligence Brief", report.intelligenceBrief);

    if (selectedCompetitor === "market_overview") {
      addSection("Momentum Signals", report.momentumSignals);
      addSection("Competitive Landscape", report.competitiveLandscape);
      addSection("Threat Assessment", report.threatAssessment);
      addSection("Your Position", report.yourPosition);
      addSection("Recommendations", report.recommendations);
    } else {
      addSection("Content Intelligence", report.contentIntelligence);
      addSection("Strategic Analysis", report.strategicAnalysis);
      addSection("Recommendations — Marketing", report.recommendationsMarketing);
      addSection("Recommendations — Sales", report.recommendationsSales);
      addSection("Threat Level", report.threatLevel);
    }

    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: "right" });
      pdf.text("PorterIQ Confidential", margin, pageHeight - 10);
    }

    pdf.save(`PorterIQ_Report_${selectedCompetitor || "Competitor"}.pdf`);
  } catch (err) {
    console.error("PDF Generation Error:", err);
  }
};
